import type { ApiClient } from '@up2d8/api-client';
import { queueEntry, queuedEntries, removeQueued, updateQueued } from './db';
import {
  MAX_ATTEMPTS_PER_ROUND,
  decideRetry,
  inSendOrder,
  isExpired,
  outboxKeyOf,
  type OutboxEntry,
  type OutboxKind,
} from './outbox-rules';

/**
 * De wachtrij: wat er offline gebeurde en nog verstuurd moet worden (Stap 12).
 *
 * Eén regel is hier belangrijker dan alle andere: **een bevestiging mag niet verdwijnen.** Ze is
 * bewijs (ADR-0042), en een medewerker die in een koelcel op "Gelezen en begrepen" drukt, heeft zijn
 * deel gedaan. Dat de telefoon geen bereik had, is niet zijn probleem.
 *
 * Daarom: in IndexedDB voordat het verzoek uitgaat, en pas weg als de server het aannam of expliciet
 * weigerde. Elk pad dat een regel weggooit, staat in `outbox-rules.ts` met een reden erbij.
 */

export interface FlushResult {
  verzonden: number;
  gebleven: number;
  /** Berichten waarvan de tekst gewijzigd is; de app moet die opnieuw laten lezen. */
  conflicten: string[];
  /**
   * Sleutels van regels die de server weigerde (conflict of `400/403/404/422`) en die dus weg zijn.
   * Wie net zelf een regel in de wachtrij zette, kan hier zien dat die niet aankwam.
   */
  geweigerd: string[];
  /** De soorten regels die aankwamen; zegt welke schermen opnieuw moeten ophalen. */
  verzondenSoorten: OutboxKind[];
}

/**
 * Checklist-items die de server net aannam, met het moment waarop (hertest 29 okt.).
 *
 * Alleen in het geheugen van deze pagina, en alleen om het scherm niet even terug te laten springen
 * tussen "uit de wachtrij" en "verse gegevens binnen": zie `stillShownAsLocal`. Na tien minuten is
 * er al lang opnieuw opgehaald.
 */
const recentlySent = new Map<string, { entry: OutboxEntry; sentAt: number }>();
const RECENTLY_SENT_MS = 10 * 60 * 1000;

function rememberSent(entry: OutboxEntry): void {
  if (entry.kind !== 'checklist-item') return;
  const now = Date.now();
  for (const [key, sent] of recentlySent) {
    if (now - sent.sentAt > RECENTLY_SENT_MS) recentlySent.delete(key);
  }
  recentlySent.set(entry.id, { entry, sentAt: now });
}

/**
 * Zet een handeling in de wachtrij.
 *
 * Geeft de sleutel terug, zodat de app kan tonen dat er iets op verzending wacht — de badge bij een
 * bericht hangt daaraan.
 */
export async function enqueue(input: {
  kind: OutboxEntry['kind'];
  /** Een bericht, of een checklist-uitvoering bij `checklist-item` (Stap 38). */
  postId: string;
  postVersion?: number;
  languageShown?: string;
  clientOccurredAt?: string;
  itemId?: string;
  value?: string;
  comment?: string;
}): Promise<string> {
  const clientOccurredAt = input.clientOccurredAt ?? new Date().toISOString();
  const id = outboxKeyOf({
    kind: input.kind,
    postId: input.postId,
    clientOccurredAt,
    itemId: input.itemId,
  });

  await queueEntry({
    id,
    kind: input.kind,
    postId: input.postId,
    clientOccurredAt,
    postVersion: input.postVersion,
    languageShown: input.languageShown,
    itemId: input.itemId,
    value: input.value,
    comment: input.comment,
    attempts: 0,
  });

  return id;
}

/**
 * Probeert de hele wachtrij te versturen.
 *
 * Wordt aangeroepen bij het openen van de app, bij het `online`-event, en door de service worker via
 * Background Sync. Alle drie kunnen tegelijk gebeuren; dat is veilig, want de server houdt bij
 * dezelfde bevestiging maar één rij (de primaire sleutel doet dat werk, niet de idempotency-header).
 *
 * Eén voor één en niet parallel: het gaat om een handvol regels, en bij een netwerk dat net weer
 * werkt is een stortvloed van verzoeken de slechtste eerste stap.
 */
export async function flushOutbox(api: ApiClient, now = new Date()): Promise<FlushResult> {
  const entries = inSendOrder(await queuedEntries());
  const result: FlushResult = {
    verzonden: 0,
    gebleven: 0,
    conflicten: [],
    geweigerd: [],
    verzondenSoorten: [],
  };

  for (const entry of entries) {
    if (isExpired(entry, now)) {
      // Zelf opruimen in plaats van wachten tot de server `422` zegt: anders blijft de badge "wacht
      // op verzending" staan bij een bevestiging die nooit meer aankomt.
      await removeQueued(entry.id);
      continue;
    }

    const status = await send(api, entry);
    const decision = status === 'ok' ? 'ok' : decideRetry(status);

    if (decision === 'ok') {
      await removeQueued(entry.id);
      rememberSent(entry);
      result.verzonden += 1;
      if (!result.verzondenSoorten.includes(entry.kind)) result.verzondenSoorten.push(entry.kind);
      continue;
    }

    if (decision === 'conflict') {
      await removeQueued(entry.id);
      result.conflicten.push(entry.postId);
      result.geweigerd.push(entry.id);
      continue;
    }

    if (decision === 'weggooien') {
      await removeQueued(entry.id);
      result.geweigerd.push(entry.id);
      continue;
    }

    const attempts = entry.attempts + 1;
    if (attempts >= MAX_ATTEMPTS_PER_ROUND) {
      // Genoeg voor nu. De regel blijft staan en gaat mee bij de volgende gelegenheid; doorgaan zou
      // bij een server die plat ligt alleen de batterij kosten.
      await updateQueued({ ...entry, attempts: 0 });
      result.gebleven += 1;
      break;
    }

    await updateQueued({ ...entry, attempts });
    result.gebleven += 1;
  }

  return result;
}

/** Verstuurt één regel. Geeft `'ok'`, een HTTP-status, of `null` bij een netwerkfout. */
async function send(api: ApiClient, entry: OutboxEntry): Promise<'ok' | number | null> {
  try {
    if (entry.kind === 'seen') {
      const { error, response } = await api.POST('/api/v1/feed/{postId}/seen', {
        params: { path: { postId: entry.postId } },
        body: { clientOccurredAt: entry.clientOccurredAt } as never,
        headers: { 'Idempotency-Key': entry.id },
      });
      return error ? (response?.status ?? null) : 'ok';
    }

    if (entry.kind === 'checklist-item') {
      // `PUT` en met het tijdstip van het toestel erbij: wie in een koelcel afvinkt en pas bij de
      // kantine netwerk heeft, hoort 06:55 in het bewijs te hebben staan (ADR-0047). Tweemaal
      // versturen levert hetzelfde resultaat op — dat is wat `PUT` belooft.
      const { error, response } = await api.PUT('/api/v1/me/checklists/{runId}/items/{itemId}', {
        params: { path: { runId: entry.postId, itemId: entry.itemId ?? '' } },
        body: {
          value: entry.value ?? '',
          fileIds: [],
          comment: entry.comment ?? '',
          clientOccurredAt: entry.clientOccurredAt,
        } as never,
        headers: { 'Idempotency-Key': entry.id },
      });
      return error ? (response?.status ?? null) : 'ok';
    }

    const { error, response } = await api.POST('/api/v1/feed/{postId}/acknowledge', {
      params: { path: { postId: entry.postId } },
      body: {
        postVersion: entry.postVersion ?? 1,
        languageShown: entry.languageShown ?? 'nl',
        clientOccurredAt: entry.clientOccurredAt,
      } as never,
      headers: { 'Idempotency-Key': entry.id },
    });
    return error ? (response?.status ?? null) : 'ok';
  } catch {
    // Een netwerkfout gooit; dat is geen oordeel van de server en dus "later".
    return null;
  }
}

/** Staat er nog iets te wachten? Voor de badge bij een bericht en de teller in de banner. */
export async function pendingPostIds(): Promise<Set<string>> {
  const entries = await queuedEntries();
  return new Set(
    entries.filter((entry) => entry.kind === 'acknowledge').map((entry) => entry.postId),
  );
}

/** Wat er van een checklist-item lokaal getoond wordt, tot de server het bevestigt. */
export interface PendingChecklistEntry {
  value: string;
  comment: string;
  occurredAt: string;
  /** Wanneer de server het aannam; `null` zolang het in de wachtrij staat. */
  sentAt: number | null;
}

/**
 * Wat er per checklist-item op verzending wacht, met de waarde erbij (VLR-2).
 *
 * Het afvinkscherm toont een item dat offline afgevinkt werd als afgevinkt — met de waarde, het
 * tijdstip en het klokje — en niet als een open item met een klokje ernaast. Wie "Ja" tikte, hoort
 * "Ja" te zien staan, ook als de server het nog niet weet.
 *
 * Ook wat net verzonden is staat erin, met `sentAt`: het scherm houdt het als afgevinkt tot zijn
 * gegevens van ná de verzending zijn (`stillShownAsLocal`, hertest 29 okt.).
 */
export async function pendingChecklistEntries(): Promise<Map<string, PendingChecklistEntry>> {
  const entries = await queuedEntries();
  const result = new Map<string, PendingChecklistEntry>();
  const keyOf = (entry: OutboxEntry) => `${entry.postId}:${entry.itemId ?? ''}`;

  // Eerst wat al verzonden is; een regel die nog wacht, is nieuwer en overschrijft het hieronder.
  for (const { entry, sentAt } of recentlySent.values()) {
    result.set(keyOf(entry), {
      value: entry.value ?? '',
      comment: entry.comment ?? '',
      occurredAt: entry.clientOccurredAt,
      sentAt,
    });
  }
  for (const entry of inSendOrder(entries.filter((item) => item.kind === 'checklist-item'))) {
    // In verzendvolgorde: de laatste tik op hetzelfde item wint, net als op de server.
    result.set(keyOf(entry), {
      value: entry.value ?? '',
      comment: entry.comment ?? '',
      occurredAt: entry.clientOccurredAt,
      sentAt: null,
    });
  }
  return result;
}
